import { apiFetch } from "./client";

export interface McpAuthConfig {
  type: "none" | "api_key" | "bearer" | "oauth_client_credentials" | "oauth_signin";
  apiKeyHeader?: string;
  apiKeyValue?: string;
  bearerToken?: string;
  oauthTokenUrl?: string;
  oauthClientId?: string;
  oauthClientSecret?: string;
  oauthScope?: string;
  // oauth_signin: optional scope override; all live state is server-side.
  oauthSigninScope?: string;
}

export interface McpOAuthStatus {
  status: "not_configured" | "pending" | "connected" | "error";
  signedInAs?: string | null;
  expiresAt?: string | null;
  scopes?: string | null;
  lastError?: string | null;
  resource?: string | null;
}

export interface McpHttpConfig {
  url: string;
  headers?: Record<string, string>;
  auth?: McpAuthConfig;
}

export interface McpStdioConfig {
  command: string;
  args?: string[];
  env?: Record<string, string>;
}

export type McpSecretScope = "global" | "tenant";

export interface McpCatalogueItem {
  id: string;
  // null = platform-managed MCP; set = tenant-owned MCP
  companyId: string | null;
  name: string;
  displayName: string;
  description: string | null;
  transportType: "http" | "sse" | "stdio";
  icon: string | null;
  httpConfig: McpHttpConfig | null;
  stdioConfig: McpStdioConfig | null;
  toolSchemaCache: Record<string, McpToolSchema> | null;
  toolSchemaUpdatedAt: string | null;
  secretScope: McpSecretScope;
  vaultSecretRef: string | null;
  version: string | null;
  status: string;
  // Platform-managed MCPs only. true = every tenant sees it; false =
  // visibility limited to `allocatedCompanyIds`.
  isAvailableToAll: boolean;
  allocatedCompanyIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface McpToolSchema {
  name: string;
  description?: string;
  inputSchema?: unknown;
}

export interface McpTestResult {
  success: boolean;
  tools?: Array<{ name: string; description?: string; inputSchema?: unknown }>;
  error?: string;
}

export interface TenantMcp {
  id: string;
  companyId: string;
  catalogueMcpId: string | null;
  name: string;
  customConfig: Record<string, unknown> | null;
  credentialsVault: string | null;
  allowedTools: string[] | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentMcp {
  id: string;
  agentId: string;
  tenantMcpId: string;
  configOverrides: Record<string, unknown> | null;
  allowedTools: string[] | null;
  enabled: boolean;
  assignedAt: string;
  // Sync state — written by triggerMcpSync after each push to the runtime.
  syncStatus: "pending" | "synced" | "skipped" | "failed";
  syncError: string | null;
  syncedAt: string | null;
  // Joined fields from tenant_mcps + mcp_catalogue (added by listByAgent)
  // so the chat-composer Tools tab can render without extra round-trips.
  tenantMcpName?: string | null;
  tenantMcpEnabled?: boolean | null;
  tenantAllowedTools?: string[] | null;
  transportType?: "http" | "sse" | "stdio" | null;
  availableTools?: string[];
  effectiveTools?: string[];
}

export interface EffectiveToolsResult {
  agentMcpId?: string;
  tenantMcpId?: string;
  availableTools: string[];
  tenantAllowedTools: string[] | null;
  agentAllowedTools?: string[] | null;
  effectiveTools: string[];
  allowedTools?: string[] | null;
}

export interface McpSyncResult {
  success: boolean;
  serverCount?: number;
  error?: string;
  added?: string[];
  removed?: string[];
  updated?: string[];
  unchanged?: string[];
}

export interface DiscoveredMcp {
  id: string;
  agentId: string;
  companyId: string;
  name: string;
  transportType: string | null;
  config: Record<string, unknown> | null;
  status: "discovered" | "promoted" | "removed";
  promotedToTenantMcpId: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface DiscoveryResult {
  discovered: Array<{ name: string; transportType: string; config: Record<string, unknown> }>;
  platformManaged: string[];
  removed: string[];
  error?: string;
}

export const mcpApi = {
  // Catalogue. Pass `companyId` to filter: a UUID scopes the list to that
  // tenant (plus platform-wide rows); "platform" returns platform-only; omit
  // for all (instance admins only).
  listCatalogue: (opts?: { companyId?: string }) => {
    const qs = opts?.companyId ? `?companyId=${encodeURIComponent(opts.companyId)}` : "";
    return apiFetch<McpCatalogueItem[]>(`/mcp/catalogue${qs}`);
  },
  getCatalogue: (id: string) => apiFetch<McpCatalogueItem>(`/mcp/catalogue/${id}`),
  createCatalogue: (data: Record<string, unknown>) =>
    apiFetch<McpCatalogueItem>("/mcp/catalogue", { method: "POST", body: JSON.stringify(data) }),
  updateCatalogue: (id: string, data: Record<string, unknown>) =>
    apiFetch<McpCatalogueItem>(`/mcp/catalogue/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteCatalogue: (id: string) =>
    apiFetch<void>(`/mcp/catalogue/${id}`, { method: "DELETE" }),

  // Test
  test: (data: Record<string, unknown>) =>
    apiFetch<McpTestResult>("/mcp/test", { method: "POST", body: JSON.stringify(data) }),

  // OAuth Sign-In (Case 1: the MCP server runs its own OAuth)
  oauthStart: (id: string, opts?: { scope?: string; loginHint?: string }) =>
    apiFetch<{ authorizeUrl: string; redirectUri: string }>(
      `/mcp/catalogue/${id}/oauth/start`,
      {
        method: "POST",
        body: JSON.stringify({
          ...(opts?.scope ? { scope: opts.scope } : {}),
          ...(opts?.loginHint ? { loginHint: opts.loginHint } : {}),
        }),
      },
    ),
  oauthStatus: (id: string) =>
    apiFetch<McpOAuthStatus>(`/mcp/catalogue/${id}/oauth/status`),
  oauthDisconnect: (id: string) =>
    apiFetch<{ ok: boolean }>(`/mcp/catalogue/${id}/oauth/disconnect`, { method: "POST" }),

  // Refresh schema
  refreshSchema: (id: string, tools: unknown[]) =>
    apiFetch<McpCatalogueItem>(`/mcp/catalogue/${id}/refresh-schema`, {
      method: "POST",
      body: JSON.stringify({ tools }),
    }),

  // Catalogue tools
  getCatalogueTools: (id: string) =>
    apiFetch<{ mcpId: string; mcpName: string; tools: Array<{ name: string; schema: McpToolSchema }> }>(
      `/mcp/catalogue/${id}/tools`,
    ),

  // Tenant MCPs
  listTenantMcps: (companyId: string) => apiFetch<TenantMcp[]>(`/mcp/tenants/${companyId}`),
  assignTenantMcp: (companyId: string, data: Record<string, unknown>) =>
    apiFetch<TenantMcp>(`/mcp/tenants/${companyId}`, { method: "POST", body: JSON.stringify(data) }),
  updateTenantMcp: (companyId: string, id: string, data: Record<string, unknown>) =>
    apiFetch<TenantMcp>(`/mcp/tenants/${companyId}/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  removeTenantMcp: (companyId: string, id: string) =>
    apiFetch<void>(`/mcp/tenants/${companyId}/${id}`, { method: "DELETE" }),
  getTenantMcpEffectiveTools: (companyId: string, id: string) =>
    apiFetch<EffectiveToolsResult>(`/mcp/tenants/${companyId}/${id}/effective-tools`),

  // Agent MCPs
  listAgentMcps: (agentId: string) => apiFetch<AgentMcp[]>(`/mcp/agents/${agentId}`),
  assignAgentMcp: (agentId: string, data: Record<string, unknown>) =>
    apiFetch<AgentMcp>(`/mcp/agents/${agentId}`, { method: "POST", body: JSON.stringify(data) }),
  updateAgentMcp: (agentId: string, id: string, data: Record<string, unknown>) =>
    apiFetch<AgentMcp>(`/mcp/agents/${agentId}/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  removeAgentMcp: (agentId: string, id: string) =>
    apiFetch<void>(`/mcp/agents/${agentId}/${id}`, { method: "DELETE" }),
  getAgentMcpEffectiveTools: (agentId: string, id: string) =>
    apiFetch<EffectiveToolsResult>(`/mcp/agents/${agentId}/${id}/effective-tools`),

  // Sync to agent (uses adapter-ops which handles sidecar/SSH fallback)
  syncToAgent: (agentId: string) =>
    apiFetch<McpSyncResult>(`/agents/${agentId}/sync-mcp`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  // Discovery — read MCPs from agent and diff against platform-managed
  discoverFromAgent: (agentId: string) =>
    apiFetch<DiscoveryResult>(`/agents/${agentId}/discover-mcps`, { method: "POST" }),
  listDiscovered: (agentId: string) =>
    apiFetch<DiscoveredMcp[]>(`/agents/${agentId}/discovered-mcps`),
  removeDiscovered: (agentId: string, id: string) =>
    apiFetch<void>(`/agents/${agentId}/discovered-mcps/${id}`, { method: "DELETE" }),
  promoteDiscovered: (agentId: string, id: string, data?: { targetAgentIds?: string[] }) =>
    apiFetch<{ success: boolean; tenantMcpId?: string }>(`/agents/${agentId}/discovered-mcps/${id}/promote`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),
};
